import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import formatNumber from 'app/utils/formatting';
import { useMemo } from 'react';

interface ArchivePotMainViewProps {
  currentBalance: number;
  pendingCount: number;
  hasPendingApprovals: boolean;
  hasBalance: boolean;
  onViewPendingApprovals: () => void;
  onViewTransferPoints: () => void;
  onConfirm: () => void;
  isArchiving?: boolean;
}

function ArchivePotMainView({
  pendingCount,
  currentBalance,
  hasPendingApprovals,
  hasBalance,
  onViewPendingApprovals,
  onViewTransferPoints,
  onConfirm,
  isArchiving = false,
}: ArchivePotMainViewProps) {
  const renderContent = useMemo(() => {
    if (hasPendingApprovals) {
      return (
        <VStack spacing={3} align="stretch">
          <Text>
            <Text as="span" fontWeight="bold">
              {pendingCount} pending approval{pendingCount !== 1 ? 's' : ''}
            </Text>{' '}
            must be completed before this pot can be archived.
          </Text>
        </VStack>
      );
    }

    if (hasBalance) {
      return (
        <VStack spacing={3} align="stretch">
          <Text>
            This pot still has a balance of{' '}
            <Text as="span" fontWeight="bold">
              {formatNumber(currentBalance)} points
            </Text>
            . Transfer the remaining balance before archiving.
          </Text>
        </VStack>
      );
    }

    return (
      <Text>
        This pot will be removed but all activity and history will remain visible in reports and
        audit trails.
      </Text>
    );
  }, [hasBalance, hasPendingApprovals, pendingCount, currentBalance]);

  return (
    <VStack spacing={6} align="stretch">
      {renderContent}

      <HStack spacing={3} justify="flex-end">
        {hasPendingApprovals && (
          <Button variant="outline" onClick={onViewPendingApprovals}>
            View pending approvals
          </Button>
        )}

        {!hasPendingApprovals && hasBalance && (
          <Button variant="outline" onClick={onViewTransferPoints}>
            Transfer points
          </Button>
        )}

        <Button
          variant="danger"
          isDisabled={hasPendingApprovals || hasBalance}
          onClick={onConfirm}
          isLoading={isArchiving}
        >
          Archive
        </Button>
      </HStack>
    </VStack>
  );
}

export default ArchivePotMainView;
